import { InflateRequestMode, VehicleTileData } from '@sccs/types';
import { parse, pipe, rawTransform } from 'valibot';
import { getVehicleTileDataSchema } from '../schema/GetVehicleTileDataSchema';
import { SchemaAny } from '../schema/VehicleTileDataSchema';
import { MixedDlsTeaserResponse } from '../types/dls/dls-response/ProductDto';
import { MixedDlsSearchAlternativeReponseWithId } from '../types/dls/dls-response/SearchResponseDto';
import { AdditionalConfigurationData } from '../types/GeneralParameter';
import {
  isDlsSearchAlternative,
  isDlsTeaserList,
  isDlsTeaserResponse,
  isOneSearchGraphQlResponse,
  isOneSearchPreferenceResponse,
  isOneSearchResponse,
  isOneSearchSearchResponse,
  isT4Personalized,
  isVissVehicleTileDataQueryResponse,
  MixedT4Data,
  OptionalResponseTypes,
} from '../types/GeneralTypes';
import { MixedOneSearchPreferenceResponse } from '../types/one-search/onesearch-response/OfferTeaserResponseDto';
import { MixedOneSearchGraphQlResponse } from '../types/one-search/onesearch-response/OneSearchGraphQlResponse';
import { MixedOneSearchSearchResponse } from '../types/one-search/onesearch-response/SearchResponseDto';
import { TranslationType } from '../util/i18n';

export function buildVehicleTileData(
  resultData: OptionalResponseTypes,
  config: AdditionalConfigurationData,
  translations: (key: string, dataset?: TranslationType) => string,
  overrideData?: VehicleTileData,
): VehicleTileData[] {
  if (isDlsTeaserList(resultData)) {
    if (resultData.productDtoList.length === 0)
      throw Error(
        'Dls Teaser. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const processedVehicleTileDataArray = resultData.productDtoList.map((data) => {
      const mixedData: MixedDlsTeaserResponse = {
        ...data,
        market: config.market,
        lang: config.lang,
        ctx: config.ctx,
        mode: config.mode,
        responseTypeId: 'ProductDto',
        translations: translations,
      };
      const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
      return parsedVehicleTileData;
    });
    return processedVehicleTileDataArray;
  }

  if (isDlsTeaserResponse(resultData)) {
    if (resultData == null)
      throw Error(
        'Dls Teaser. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const mixedData: MixedDlsTeaserResponse = {
      ...resultData,
      market: config.market,
      lang: config.lang,
      ctx: config.ctx,
      mode: config.mode,
      translations: translations,
    };
    const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
    return [parsedVehicleTileData];
  }

  if (isDlsSearchAlternative(resultData)) {
    if (config.mode === InflateRequestMode.ZERO_RESULT) {
      if (resultData.firstVehicles?.length === 0)
        throw Error(
          'Dls Alternative. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
        );
      const processedVehicleTileDataArray = resultData.firstVehicles?.map((data) => {
        const mixedData: MixedDlsTeaserResponse = {
          ...data,
          market: config.market,
          mode: config.mode,
          responseTypeId: 'ProductDto',
          lang: config.lang,
          ctx: config.ctx,
          translations,
        };
        const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
        return parsedVehicleTileData;
      });
      return processedVehicleTileDataArray ?? [];
    } else {
      if (resultData == null)
        throw Error(
          'Dls Alternative. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
        );
      const mixedData: MixedDlsSearchAlternativeReponseWithId = {
        ...resultData,
        market: config.market,
        responseTypeId: 'SearchResponseDTO',
        lang: config.lang,
        brand: config.brand,
        ctx: config.ctx,
        mode: config.mode,
        translations,
      };
      const processedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
      return [processedVehicleTileData];
    }
  }

  if (isOneSearchResponse(resultData)) {
    if (resultData == null)
      throw Error(
        'OneSearch Preference / OfferTeaserDto. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const mixedData: MixedOneSearchPreferenceResponse = {
      ...resultData,
      market: config.market,
      brand: config.brand,
      lang: config.lang,
      ctx: config.ctx,
      mode: config.mode,
      responseTypeId: 'OfferTeaserDto',
      recommendationId: resultData.recommendationId,
      translations: translations,
    };
    const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
    return [parsedVehicleTileData];
  }

  if (isOneSearchPreferenceResponse(resultData)) {
    const oneSearchResponse = resultData.results;
    if (oneSearchResponse == null || oneSearchResponse.length === 0)
      throw Error(
        'OneSearch Preference. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const processedVehicleTileDataArray = oneSearchResponse.map((data) => {
      const mixedData: MixedOneSearchPreferenceResponse = {
        ...data,
        market: config.market,
        brand: config.brand,
        lang: config.lang,
        ctx: config.ctx,
        mode: config.mode,
        responseTypeId: 'OfferTeaserDto',
        recommendationId: resultData.recommendationId,
        cookie: config.cookie,
        translations: translations,
      };
      const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
      return parsedVehicleTileData;
    });
    return processedVehicleTileDataArray;
  }

  if (isOneSearchSearchResponse(resultData)) {
    const oneSearchResponse = resultData.results;
    if (oneSearchResponse == null || oneSearchResponse.length === 0)
      throw Error(
        'OneSearch Search. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const processedVehicleTileDataArray = oneSearchResponse.map((data) => {
      const mixedData: MixedOneSearchSearchResponse = {
        ...data,
        market: config.market,
        lang: config.lang,
        translations,
        brand: config.brand,
        ctx: config.ctx,
        responseTypeId: 'VehicleDto',
        mode: config.mode,
        dlsUrl: resultData.url,
        rawData: resultData.rawData,
      };
      const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
      return parsedVehicleTileData;
    });
    return processedVehicleTileDataArray;
  }

  if (isOneSearchGraphQlResponse(resultData)) {
    const oneSearchResponse = resultData.data.search?.results;
    if (oneSearchResponse == null || oneSearchResponse.length === 0)
      throw Error(
        'OneSearch GraphQL. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const processedVehicleTileDataArray = oneSearchResponse.map((data) => {
      const mixedData: MixedOneSearchGraphQlResponse = {
        ...data,
        market: config.market,
        brand: config.brand,
        lang: config.lang,
        mode: config.mode,
        responseTypeId: 'OneSearchGraphQlVehicle',
        cookie: config.cookie,
        translations: translations,
      };
      const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
      return parsedVehicleTileData;
    });
    return processedVehicleTileDataArray;
  }

  if (isT4Personalized(resultData)) {
    if (resultData.personalizedVehicle == null)
      throw Error(
        'T4 Personalization. No matching vehicle could be found based on the provided parameters. Please adjust your criteria and try again.',
      );
    const mixedData: MixedT4Data = {
      personalizedVehicle: resultData.personalizedVehicle,
      market: config.market,
      lang: config.lang,
      responseTypeId: 'T4VehicleDto',
      translations: translations,
    };
    const parsedVehicleTileData = parse(getVehicleTileDataSchema(mixedData), mixedData);
    return [parsedVehicleTileData];
  }

  if (isVissVehicleTileDataQueryResponse(resultData)) {
    const parsedVehicleTileData = parse(
      pipe(
        SchemaAny,
        getVehicleTileDataSchema(resultData),
        rawTransform<VehicleTileData, VehicleTileData>(({ dataset }) => {
          return applyOverrides(dataset.value, overrideData);
        }),
      ),
      resultData,
    );
    return [parsedVehicleTileData];
  }

  throw new Error('The response does not match Dls Response-Type nor Onesearch Response-Type');
}

const applyOverrides = (
  vehicleTileDataSource: VehicleTileData,
  VehicleTileDataOverride?: VehicleTileData,
): VehicleTileData => deepMergeObjects(vehicleTileDataSource, VehicleTileDataOverride);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deepMergeObjects = (obj1: any, obj2?: any) => {
  const result = { ...obj1 };

  for (const key in obj2) {
    if (obj2[key]) {
      if (obj2[key] instanceof Object && obj1[key] instanceof Object) {
        result[key] = deepMergeObjects(obj1[key], obj2[key]);
      } else {
        result[key] = obj2[key];
      }
    }
  }
  return result;
};
